/**
 * «Τι άλλαξε;» της σελίδας «Κατάλογος»: η διαφορά προχείρου ↔ server ως λίστα «παλιό → νέο», με
 * αναίρεση ανά γραμμή. Από την ίδια λίστα βγαίνουν και τα κίτρινα πεδία, οι τελείες στις καρτέλες
 * και το «N αλλαγές σε M προϊόντα» της μπάρας — ένας υπολογισμός, όχι τρεις.
 */
import type { TFunction } from 'i18next';
import { formatEuro } from '../../utils/formatEuro';
import { feeModeOf, isFeeCategory, StateFeeRow } from '../../utils/feeForm';
import {
  CategoryForm, FormIngredient, FormSize, GroupRef, ProductForm, sameMoney, treeKeys,
} from './catalogModel';
import {
  CatalogBase, CatalogDraft, ProductState, baseProduct, currentTree, moveCategory, parentsOf,
  removeNewCategory, removeNewGroup, removeNewIngredient, removeNewProduct, treeOrderChanged, updateCategory,
  updateProduct, updateProductForm,
} from './catalogDraft';

export type PanelTab = 'basics' | 'sizes' | 'options' | 'ingredients';

type SizePart = 'name' | 'price_modifier' | 'price_modifier_take_away' | 'price_modifier_delivery' | 'price_modifier_dine_in';
type IngPart = 'is_default' | 'is_removable' | 'extra_price_override';

export type RevertOp =
  | { t: 'pfield'; key: string; field: keyof ProductForm }
  | { t: 'fee'; key: string }
  | { t: 'size'; key: string; uid: string; part: SizePart }
  | { t: 'sizeDefault'; key: string }
  | { t: 'sizeAdded'; key: string; uid: string }
  | { t: 'sizeRemoved'; key: string; uid: string }
  | { t: 'sizeOrder'; key: string }
  | { t: 'ing'; key: string; uid: string; part: IngPart }
  | { t: 'ingAdded'; key: string; uid: string }
  | { t: 'ingRemoved'; key: string; uid: string }
  | { t: 'ingOrder'; key: string }
  | { t: 'groups'; key: string }
  | { t: 'flag'; key: string; flag: 'is_available' | 'is_hidden' | 'is_kiosk_recommended' }
  | { t: 'newProduct'; key: string }
  | { t: 'cfield'; key: string; field: keyof CategoryForm }
  | { t: 'cparent'; key: string }
  | { t: 'cgroups'; key: string }
  | { t: 'newCategory'; key: string }
  | { t: 'tree' }
  | { t: 'porder'; node: string }
  | { t: 'newGroup'; ref: string }
  | { t: 'newSizeGroup'; ref: string }
  | { t: 'newIngredient'; ref: string };

export interface ChangeEntry {
  id: string;
  /** p:/np: προϊόν, c:/nc: κατηγορία, 'catalog' για τα υπόλοιπα */
  owner: string;
  title: string;
  what: string;
  from: string;
  to: string;
  money: boolean;
  revert: RevertOp;
  /** καρτέλα του πάνελ προϊόντος (τελεία «έχει αλλαγές») */
  tab?: PanelTab;
  /** πεδίο που βάφεται κίτρινο (π.χ. 'name', 'size:s12:price_modifier') */
  field?: string;
}

export interface ChangeContext {
  t: TFunction;
  /** διαδρομή κατηγορίας («Καφέδες › Κρύοι») για αναφορά της φόρμας */
  categoryPath: (ref: ProductForm['category_id']) => string;
  /** όνομα κόμβου (κατηγορία ή «Χωρίς κατηγορία») */
  nodeName: (key: string | null) => string;
  groupName: (ref: GroupRef) => string;
  supplierName: (id: number | undefined) => string;
  stateFees: StateFeeRow[];
}

const DASH = '—';

const yesNo = (t: TFunction, v: unknown) => (v ? t('catalog:common.yes') : t('catalog:common.no'));
const pct = (v: unknown) => (v === undefined || v === null || v === '' ? DASH : `${Number(v)}%`);
const text = (v: unknown) => (v === undefined || v === null || String(v).trim() === '' ? DASH : String(v));
const money = (v: number | null | undefined) => (v === null || v === undefined ? DASH : formatEuro(v));

export function toneLabel(t: TFunction, tone: string | null | undefined, kind: 'product' | 'category'): string {
  if (!tone) return kind === 'category' ? t('catalog:color.auto') : t('catalog:color.none');
  return t(`products:catalogColor.tones.${tone}`);
}

export function productTypeLabel(t: TFunction, type: string | null | undefined): string {
  if (type === 'ΠΡΟΙΟΝ') return t('catalog:types.product');
  if (type === 'ΥΠΗΡΕΣΙΑ') return t('catalog:types.service');
  if (type === 'ΕΜΠΟΡΕΥΜΑ') return t('catalog:types.goods');
  return type ? String(type) : DASH;
}

export function feeLabel(t: TFunction, form: Pick<ProductForm, 'fee_category' | 'auto_plastic_fee_count'>): string {
  const mode = feeModeOf(form);
  if (mode === 'fee') return t('catalog:fees.isFeeShort', { code: form.fee_category });
  if (mode === 'trigger') return t('catalog:fees.triggerShort', { count: form.auto_plastic_fee_count });
  return t('catalog:fees.noneShort');
}

type FieldDef = { field: keyof ProductForm; label: string; tab: PanelTab; fmt: (v: unknown, ctx: ChangeContext) => string };

const PRODUCT_FIELDS: FieldDef[] = [
  { field: 'name', label: 'catalog:fields.name', tab: 'basics', fmt: text },
  { field: 'description', label: 'catalog:fields.description', tab: 'basics', fmt: text },
  { field: 'category_id', label: 'catalog:fields.category', tab: 'basics', fmt: (v, c) => c.categoryPath(v as ProductForm['category_id']) },
  { field: 'product_type', label: 'catalog:fields.type', tab: 'basics', fmt: (v, c) => productTypeLabel(c.t, v as string) },
  { field: 'vat', label: 'catalog:fields.vat', tab: 'basics', fmt: pct },
  { field: 'max_discount_percent', label: 'catalog:fields.maxDiscount', tab: 'basics', fmt: pct },
  { field: 'color', label: 'catalog:fields.color', tab: 'basics', fmt: (v, c) => toneLabel(c.t, v as string | null, 'product') },
  { field: 'is_weight_variable', label: 'catalog:fields.weighted', tab: 'basics', fmt: (v, c) => yesNo(c.t, v) },
  { field: 'is_open_price', label: 'catalog:fields.openPrice', tab: 'basics', fmt: (v, c) => yesNo(c.t, v) },
  { field: 'barcode', label: 'catalog:fields.barcode', tab: 'basics', fmt: text },
  { field: 'barcode_type', label: 'catalog:fields.barcodeType', tab: 'basics', fmt: (v, c) => (v === 'weighted' ? c.t('catalog:barcode.weighted') : c.t('catalog:barcode.standard')) },
  { field: 'sell_unit', label: 'catalog:fields.sellUnit', tab: 'basics', fmt: (v, c) => c.t(`catalog:units.${v || 'piece'}`) },
  { field: 'vat_take_away', label: 'catalog:fields.vatTakeAway', tab: 'basics', fmt: pct },
  { field: 'vat_delivery', label: 'catalog:fields.vatDelivery', tab: 'basics', fmt: pct },
  { field: 'vat_dine_in', label: 'catalog:fields.vatDineIn', tab: 'basics', fmt: pct },
  { field: 'supplier_id', label: 'catalog:fields.supplier', tab: 'basics', fmt: (v, c) => c.supplierName(v as number | undefined) },
  { field: 'min_stock_level', label: 'catalog:fields.minStock', tab: 'basics', fmt: text },
  { field: 'current_stock', label: 'catalog:fields.currentStock', tab: 'basics', fmt: text },
  { field: 'can_be_ingredient', label: 'catalog:fields.canBeIngredient', tab: 'basics', fmt: (v, c) => yesNo(c.t, v) },
  { field: 'stock_unit', label: 'catalog:fields.stockUnit', tab: 'basics', fmt: (v, c) => (v ? c.t(`catalog:stockUnits.${v}`, { defaultValue: String(v) }) : DASH) },
  { field: 'stock_unit_quantity', label: 'catalog:fields.stockUnitQuantity', tab: 'basics', fmt: text },
];

const sameValue = (a: unknown, b: unknown) => {
  const empty = (v: unknown) => v === undefined || v === null || v === '';
  if (empty(a) && empty(b)) return true;
  if (typeof a === 'number' || typeof b === 'number') return Number(a) === Number(b);
  return a === b;
};

const SIZE_PARTS: Array<{ part: SizePart; label: string }> = [
  { part: 'price_modifier', label: 'catalog:sizes.price' },
  { part: 'price_modifier_take_away', label: 'catalog:sizes.takeAway' },
  { part: 'price_modifier_dine_in', label: 'catalog:sizes.dineIn' },
  { part: 'price_modifier_delivery', label: 'catalog:sizes.delivery' },
];

/** Τιμή μεγέθους στο κανάλι όπως τη χρεώνει το ταμείο (κενό κανάλι = η «Τιμή»). */
const channelShown = (s: FormSize, part: SizePart) =>
  (part === 'price_modifier' ? s.price_modifier : (s[part] as number | null) ?? s.price_modifier);

const sameOrder = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i]);

function sizeChanges(key: string, title: string, orig: FormSize[], cur: FormSize[], ctx: ChangeContext): ChangeEntry[] {
  const { t } = ctx;
  const out: ChangeEntry[] = [];
  const byUid = new Map(orig.map(s => [s._uid, s] as [string, FormSize]));
  const curUids = new Set(cur.map(s => s._uid));
  cur.forEach(s => {
    const o = byUid.get(s._uid);
    if (!o) {
      out.push({
        id: `${key}:size+:${s._uid}`, owner: key, title, what: t('catalog:changes.sizeAdded'),
        from: DASH, to: `${s.name || DASH} · ${formatEuro(s.price_modifier)}`, money: true,
        revert: { t: 'sizeAdded', key, uid: s._uid }, tab: 'sizes', field: `size:${s._uid}`,
      });
      return;
    }
    if (o.name !== s.name) {
      out.push({
        id: `${key}:size:${s._uid}:name`, owner: key, title, what: t('catalog:changes.sizeName'),
        from: o.name, to: s.name || DASH, money: false, revert: { t: 'size', key, uid: s._uid, part: 'name' },
        tab: 'sizes', field: `size:${s._uid}:name`,
      });
    }
    SIZE_PARTS.forEach(({ part, label }) => {
      const a = o[part] as number | null;
      const b = s[part] as number | null;
      if (sameMoney(a, b)) return;
      out.push({
        id: `${key}:size:${s._uid}:${part}`, owner: key, title, what: `${s.name || o.name} · ${t(label)}`,
        from: money(channelShown(o, part)), to: money(channelShown(s, part)), money: true,
        revert: { t: 'size', key, uid: s._uid, part }, tab: 'sizes', field: `size:${s._uid}:${part}`,
      });
    });
  });
  orig.forEach(o => {
    if (curUids.has(o._uid)) return;
    out.push({
      id: `${key}:size-:${o._uid}`, owner: key, title, what: t('catalog:changes.sizeRemoved'),
      from: `${o.name} · ${formatEuro(o.price_modifier)}`, to: DASH, money: true,
      revert: { t: 'sizeRemoved', key, uid: o._uid }, tab: 'sizes',
    });
  });
  const defOrig = orig.find(s => s.is_default);
  const defCur = cur.find(s => s.is_default);
  if ((defOrig?._uid || '') !== (defCur?._uid || '') && (defCur || curUids.has(defOrig?._uid || ''))) {
    out.push({
      id: `${key}:size:default`, owner: key, title, what: t('catalog:changes.sizeDefault'),
      from: defOrig?.name || DASH, to: defCur?.name || DASH, money: false, revert: { t: 'sizeDefault', key }, tab: 'sizes',
    });
  }
  const common = (list: FormSize[], other: Set<string>) => list.filter(s => other.has(s._uid)).map(s => s._uid);
  const origUids = new Set(orig.map(s => s._uid));
  const a = common(orig, curUids);
  const b = common(cur, origUids);
  if (!sameOrder(a, b)) {
    const names = (uids: string[], list: FormSize[]) => uids.map(u => list.find(s => s._uid === u)?.name || '').join(', ');
    out.push({
      id: `${key}:size:order`, owner: key, title, what: t('catalog:changes.sizeOrder'),
      from: names(a, orig), to: names(b, cur), money: false, revert: { t: 'sizeOrder', key }, tab: 'sizes',
    });
  }
  return out;
}

function ingredientChanges(key: string, title: string, orig: FormIngredient[], cur: FormIngredient[], ctx: ChangeContext): ChangeEntry[] {
  const { t } = ctx;
  const out: ChangeEntry[] = [];
  const byUid = new Map(orig.map(i => [i._uid, i] as [string, FormIngredient]));
  const curUids = new Set(cur.map(i => i._uid));
  const flags = (i: FormIngredient) => [i.is_default ? t('catalog:ingredients.inside') : '', i.is_removable ? t('catalog:ingredients.without') : '']
    .filter(Boolean).join(' · ') || DASH;
  cur.forEach(i => {
    const o = byUid.get(i._uid);
    if (!o) {
      out.push({
        id: `${key}:ing+:${i._uid}`, owner: key, title, what: t('catalog:changes.ingredientAdded', { name: i.name }),
        from: DASH, to: flags(i), money: false, revert: { t: 'ingAdded', key, uid: i._uid }, tab: 'ingredients', field: `ing:${i._uid}`,
      });
      return;
    }
    if (o.is_default !== i.is_default) {
      out.push({
        id: `${key}:ing:${i._uid}:d`, owner: key, title, what: `${i.name} · ${t('catalog:ingredients.inside')}`,
        from: yesNo(t, o.is_default), to: yesNo(t, i.is_default), money: false,
        revert: { t: 'ing', key, uid: i._uid, part: 'is_default' }, tab: 'ingredients', field: `ing:${i._uid}:is_default`,
      });
    }
    if (o.is_removable !== i.is_removable) {
      out.push({
        id: `${key}:ing:${i._uid}:r`, owner: key, title, what: `${i.name} · ${t('catalog:ingredients.without')}`,
        from: yesNo(t, o.is_removable), to: yesNo(t, i.is_removable), money: false,
        revert: { t: 'ing', key, uid: i._uid, part: 'is_removable' }, tab: 'ingredients', field: `ing:${i._uid}:is_removable`,
      });
    }
    if (!sameMoney(o.extra_price_override, i.extra_price_override)) {
      out.push({
        id: `${key}:ing:${i._uid}:p`, owner: key, title, what: `${i.name} · ${t('catalog:ingredients.extra')}`,
        from: money(o.extra_price_override ?? o.library_price), to: money(i.extra_price_override ?? i.library_price), money: true,
        revert: { t: 'ing', key, uid: i._uid, part: 'extra_price_override' }, tab: 'ingredients', field: `ing:${i._uid}:extra_price_override`,
      });
    }
  });
  orig.forEach(o => {
    if (curUids.has(o._uid)) return;
    out.push({
      id: `${key}:ing-:${o._uid}`, owner: key, title, what: t('catalog:changes.ingredientRemoved', { name: o.name }),
      from: flags(o), to: DASH, money: false, revert: { t: 'ingRemoved', key, uid: o._uid }, tab: 'ingredients',
    });
  });
  const origUids = new Set(orig.map(i => i._uid));
  const a = orig.filter(i => curUids.has(i._uid)).map(i => i._uid);
  const b = cur.filter(i => origUids.has(i._uid)).map(i => i._uid);
  if (!sameOrder(a, b)) {
    out.push({
      id: `${key}:ing:order`, owner: key, title, what: t('catalog:changes.ingredientOrder'),
      from: a.map(u => byUid.get(u)?.name || '').join(', '), to: b.map(u => byUid.get(u)?.name || '').join(', '),
      money: false, revert: { t: 'ingOrder', key }, tab: 'ingredients',
    });
  }
  return out;
}

/** Οι αλλαγές ενός υπάρχοντος προϊόντος. */
export function productChanges(key: string, orig: ProductState, cur: ProductState, ctx: ChangeContext): ChangeEntry[] {
  const { t } = ctx;
  const title = cur.form.name || orig.form.name;
  const out: ChangeEntry[] = [];
  PRODUCT_FIELDS.forEach(def => {
    const a = orig.form[def.field];
    const b = cur.form[def.field];
    if (sameValue(a, b)) return;
    out.push({
      id: `${key}:${String(def.field)}`, owner: key, title, what: t(def.label), from: def.fmt(a, ctx), to: def.fmt(b, ctx),
      money: false, revert: { t: 'pfield', key, field: def.field }, tab: def.tab, field: String(def.field),
    });
  });
  if (orig.form.fee_category !== cur.form.fee_category || orig.form.auto_plastic_fee_count !== cur.form.auto_plastic_fee_count) {
    out.push({
      id: `${key}:fee`, owner: key, title, what: t('catalog:fields.fees'), from: feeLabel(t, orig.form), to: feeLabel(t, cur.form),
      money: false, revert: { t: 'fee', key }, tab: 'basics', field: 'fee',
    });
  }
  out.push(...sizeChanges(key, title, orig.form.sizes, cur.form.sizes, ctx));
  out.push(...ingredientChanges(key, title, orig.form.simple_ingredients, cur.form.simple_ingredients, ctx));
  if (cur.groups && orig.groups && !sameOrder(orig.groups.map(String), cur.groups.map(String))) {
    out.push({
      id: `${key}:groups`, owner: key, title, what: t('catalog:fields.optionGroups'),
      from: orig.groups.map(ctx.groupName).join(', ') || DASH, to: cur.groups.map(ctx.groupName).join(', ') || DASH,
      money: false, revert: { t: 'groups', key }, tab: 'options', field: 'groups',
    });
  }
  const flag = (f: 'is_available' | 'is_hidden' | 'is_kiosk_recommended', label: string, on: string, off: string) => {
    if (orig[f] === cur[f]) return;
    out.push({
      id: `${key}:${f}`, owner: key, title, what: t(label), from: t(orig[f] ? on : off), to: t(cur[f] ? on : off),
      money: false, revert: { t: 'flag', key, flag: f }, tab: 'basics', field: f,
    });
  };
  flag('is_available', 'catalog:fields.availability', 'catalog:status.available', 'catalog:status.unavailable');
  flag('is_hidden', 'catalog:fields.onPos', 'catalog:status.hidden', 'catalog:status.visible');
  flag('is_kiosk_recommended', 'catalog:fields.kiosk', 'catalog:common.yes', 'catalog:common.no');
  return out;
}

const CATEGORY_FIELDS: Array<{ field: keyof CategoryForm; label: string; fmt: (v: unknown, ctx: ChangeContext) => string }> = [
  { field: 'name', label: 'catalog:fields.name', fmt: text },
  { field: 'description', label: 'catalog:fields.description', fmt: text },
  { field: 'color', label: 'catalog:fields.color', fmt: (v, c) => toneLabel(c.t, v as string | null, 'category') },
  { field: 'is_hidden', label: 'catalog:fields.categoryHidden', fmt: (v, c) => yesNo(c.t, v) },
  { field: 'default_product_type', label: 'catalog:fields.defaultType', fmt: (v, c) => (v ? productTypeLabel(c.t, v as string) : DASH) },
  { field: 'default_vat', label: 'catalog:fields.defaultVat', fmt: pct },
];

/** Όλες οι αλλαγές του προχείρου, στη σειρά: κατηγορίες, δέντρο, προϊόντα, νέες ομάδες/υλικά. */
export function computeChanges(draft: CatalogDraft, base: CatalogBase, ctx: ChangeContext): ChangeEntry[] {
  const { t } = ctx;
  const out: ChangeEntry[] = [];
  const tree = currentTree(draft, base);
  const baseParents = parentsOf(base.tree);
  const draftParents = parentsOf(tree);

  // Κατηγορίες: νέες (μία γραμμή) και αλλαγμένες (ανά πεδίο).
  treeKeys(tree).forEach(key => {
    const form = draft.categories[key];
    if (draft.newCategories.includes(key)) {
      out.push({
        id: `${key}:new`, owner: key, title: form?.name || t('catalog:category.untitled'), what: t('catalog:changes.newCategory'),
        from: DASH, to: draftParents[key] ? ctx.nodeName(draftParents[key]) : t('catalog:category.root'),
        money: false, revert: { t: 'newCategory', key },
      });
      return;
    }
    const orig = base.categories[key];
    if (!orig) return;
    const title = form?.name || orig.form.name;
    if (form) {
      CATEGORY_FIELDS.forEach(def => {
        if (sameValue(orig.form[def.field], form[def.field])) return;
        out.push({
          id: `${key}:${def.field}`, owner: key, title, what: t(def.label), from: def.fmt(orig.form[def.field], ctx),
          to: def.fmt(form[def.field], ctx), money: false, revert: { t: 'cfield', key, field: def.field }, field: def.field,
        });
      });
    }
    if ((baseParents[key] ?? null) !== (draftParents[key] ?? null)) {
      out.push({
        id: `${key}:parent`, owner: key, title, what: t('catalog:fields.parent'),
        from: baseParents[key] ? ctx.nodeName(baseParents[key]) : t('catalog:category.root'),
        to: draftParents[key] ? ctx.nodeName(draftParents[key]) : t('catalog:category.root'),
        money: false, revert: { t: 'cparent', key }, field: 'parent',
      });
    }
    const groups = draft.categoryGroups[key];
    const origGroups = base.catGroups[key];
    if (groups && origGroups && !sameOrder(groups.map(String), origGroups.map(String))) {
      out.push({
        id: `${key}:groups`, owner: key, title, what: t('catalog:fields.defaultGroups'),
        from: origGroups.map(ctx.groupName).join(', ') || DASH, to: groups.map(ctx.groupName).join(', ') || DASH,
        money: false, revert: { t: 'cgroups', key }, field: 'groups',
      });
    }
  });
  if (draft.tree && treeOrderChanged(base.tree, draft.tree)) {
    out.push({
      id: 'tree:order', owner: 'catalog', title: t('catalog:changes.categoriesTitle'), what: t('catalog:changes.categoryOrder'),
      from: DASH, to: t('catalog:changes.newOrder'), money: false, revert: { t: 'tree' },
    });
  }

  // Προϊόντα: υπάρχοντα με διαφορές, μετά τα νέα.
  base.products.forEach(p => {
    const key = `p:${p.id}`;
    const cur = draft.products[key];
    if (!cur) return;
    const orig = baseProduct(base, key)!;
    out.push(...productChanges(key, orig, cur, ctx));
  });
  draft.newProducts.forEach(key => {
    const s = draft.products[key];
    if (!s) return;
    out.push({
      id: `${key}:new`, owner: key, title: s.form.name || t('catalog:product.untitled'), what: t('catalog:changes.newProduct'),
      from: DASH, to: ctx.categoryPath(s.form.category_id), money: false, revert: { t: 'newProduct', key },
    });
  });

  // Σειρά στο ταμείο ανά κατηγορία (μόνο ό,τι άλλαξε ρητά ανάμεσα στα υπάρχοντα).
  Object.keys(draft.productOrder).forEach(node => {
    const baseOrder = base.order[node] || [];
    const keep = new Set(baseOrder);
    const now = draft.productOrder[node].filter(k => keep.has(k));
    const was = baseOrder.filter(k => now.includes(k));
    if (sameOrder(was, now)) return;
    out.push({
      id: `order:${node}`, owner: 'catalog', title: ctx.nodeName(node), what: t('catalog:changes.productOrder'),
      from: DASH, to: t('catalog:changes.newOrder'), money: false, revert: { t: 'porder', node },
    });
  });

  draft.newGroups.forEach(g => out.push({
    id: `${g.ref}:new`, owner: 'catalog', title: g.name, what: t('catalog:changes.newOptionGroup'), from: DASH,
    to: g.choices.map(c => c.name).join(', ') || DASH, money: false, revert: { t: 'newGroup', ref: g.ref },
  }));
  draft.newSizeGroups.forEach(g => out.push({
    id: `${g.ref}:new`, owner: 'catalog', title: g.name, what: t('catalog:changes.newSizeGroup'), from: DASH,
    to: g.items.map(i => i.name).join(', ') || DASH, money: false, revert: { t: 'newSizeGroup', ref: g.ref },
  }));
  draft.newIngredients.forEach(i => out.push({
    id: `${i.ref}:new`, owner: 'catalog', title: i.name, what: t('catalog:changes.newIngredient'), from: DASH,
    to: formatEuro(i.price), money: true, revert: { t: 'newIngredient', ref: i.ref },
  }));
  return out;
}

/** Πόσα προϊόντα / κατηγορίες αγγίζουν οι αλλαγές (για το «N αλλαγές σε M προϊόντα»). */
export function changeOwners(changes: ChangeEntry[]): { products: number; categories: number } {
  const products = new Set<string>();
  const categories = new Set<string>();
  changes.forEach(c => {
    if (c.owner.startsWith('p:') || c.owner.startsWith('np:')) products.add(c.owner);
    else if (c.owner.startsWith('c:') || c.owner.startsWith('nc:')) categories.add(c.owner);
  });
  return { products: products.size, categories: categories.size };
}

/* ───────────────────────── Αναίρεση μίας γραμμής ───────────────────────── */

const cloneSize = (s: FormSize): FormSize => ({ ...s });

/** Βάζει πίσω τη σειρά της βάσης για όσα υπήρχαν· τα νέα μένουν στο τέλος, με τη σειρά τους. */
function restoreOrder<T extends { _uid: string }>(orig: T[], cur: T[]): T[] {
  const curBy = new Map(cur.map(x => [x._uid, x] as [string, T]));
  const kept = orig.filter(o => curBy.has(o._uid)).map(o => curBy.get(o._uid)!);
  const origUids = new Set(orig.map(o => o._uid));
  return [...kept, ...cur.filter(x => !origUids.has(x._uid))];
}

export function applyRevert(draft: CatalogDraft, base: CatalogBase, op: RevertOp): CatalogDraft {
  switch (op.t) {
    case 'pfield': {
      const orig = base.forms[op.key];
      return orig ? updateProductForm(draft, base, op.key, f => ({ ...f, [op.field]: orig[op.field] })) : draft;
    }
    case 'fee': {
      const orig = base.forms[op.key];
      if (!orig) return draft;
      return updateProductForm(draft, base, op.key, f => {
        // Από/προς τέλος αλλάζουν και τα μεγέθη/ΦΠΑ/έκπτωση (applyFeeMode): πίσω όλα μαζί.
        if (isFeeCategory(f.fee_category) !== isFeeCategory(orig.fee_category)) {
          return {
            ...f,
            fee_category: orig.fee_category,
            auto_plastic_fee_count: orig.auto_plastic_fee_count,
            vat: orig.vat,
            max_discount_percent: orig.max_discount_percent,
            sizes: orig.sizes.map(cloneSize),
          };
        }
        return { ...f, fee_category: orig.fee_category, auto_plastic_fee_count: orig.auto_plastic_fee_count };
      });
    }
    case 'size': {
      const o = base.forms[op.key]?.sizes.find(s => s._uid === op.uid);
      if (!o) return draft;
      return updateProductForm(draft, base, op.key, f => ({
        ...f, sizes: f.sizes.map(s => (s._uid === op.uid ? { ...s, [op.part]: o[op.part] } : s)),
      }));
    }
    case 'sizeDefault': {
      const orig = base.forms[op.key];
      if (!orig) return draft;
      const was = new Map(orig.sizes.map(s => [s._uid, s.is_default] as [string, boolean]));
      return updateProductForm(draft, base, op.key, f => ({ ...f, sizes: f.sizes.map(s => ({ ...s, is_default: was.get(s._uid) === true })) }));
    }
    case 'sizeAdded':
      return updateProductForm(draft, base, op.key, f => ({ ...f, sizes: f.sizes.filter(s => s._uid !== op.uid) }));
    case 'sizeRemoved': {
      const orig = base.forms[op.key];
      const idx = orig ? orig.sizes.findIndex(s => s._uid === op.uid) : -1;
      if (!orig || idx < 0) return draft;
      return updateProductForm(draft, base, op.key, f => {
        const sizes = f.sizes.slice();
        sizes.splice(Math.min(idx, sizes.length), 0, cloneSize(orig.sizes[idx]));
        return { ...f, sizes };
      });
    }
    case 'sizeOrder': {
      const orig = base.forms[op.key];
      return orig ? updateProductForm(draft, base, op.key, f => ({ ...f, sizes: restoreOrder(orig.sizes, f.sizes) })) : draft;
    }
    case 'ing': {
      const o = base.forms[op.key]?.simple_ingredients.find(i => i._uid === op.uid);
      if (!o) return draft;
      return updateProductForm(draft, base, op.key, f => ({
        ...f, simple_ingredients: f.simple_ingredients.map(i => (i._uid === op.uid ? { ...i, [op.part]: o[op.part] } : i)),
      }));
    }
    case 'ingAdded':
      return updateProductForm(draft, base, op.key, f => ({ ...f, simple_ingredients: f.simple_ingredients.filter(i => i._uid !== op.uid) }));
    case 'ingRemoved': {
      const orig = base.forms[op.key];
      const idx = orig ? orig.simple_ingredients.findIndex(i => i._uid === op.uid) : -1;
      if (!orig || idx < 0) return draft;
      return updateProductForm(draft, base, op.key, f => {
        const list = f.simple_ingredients.slice();
        list.splice(Math.min(idx, list.length), 0, { ...orig.simple_ingredients[idx] });
        return { ...f, simple_ingredients: list };
      });
    }
    case 'ingOrder': {
      const orig = base.forms[op.key];
      return orig ? updateProductForm(draft, base, op.key, f => ({ ...f, simple_ingredients: restoreOrder(orig.simple_ingredients, f.simple_ingredients) })) : draft;
    }
    case 'groups': {
      const orig = base.links[op.key] ?? null;
      return updateProduct(draft, base, op.key, s => ({ ...s, groups: orig ? orig.slice() : null }));
    }
    case 'flag': {
      const orig = baseProduct(base, op.key);
      return orig ? updateProduct(draft, base, op.key, s => ({ ...s, [op.flag]: orig[op.flag] })) : draft;
    }
    case 'newProduct':
      return removeNewProduct(draft, op.key);
    case 'cfield': {
      const orig = base.categories[op.key]?.form;
      return orig ? updateCategory(draft, base, op.key, f => ({ ...f, [op.field]: orig[op.field] })) : draft;
    }
    case 'cparent': {
      const baseParent = parentsOf(base.tree)[op.key] ?? null;
      const list = baseParent ? base.tree.children[baseParent] || [] : base.tree.roots;
      return moveCategory(draft, base, op.key, baseParent, Math.max(0, list.indexOf(op.key)));
    }
    case 'cgroups': {
      const categoryGroups = { ...draft.categoryGroups };
      delete categoryGroups[op.key];
      return { ...draft, categoryGroups };
    }
    case 'newCategory':
      return removeNewCategory(draft, base, op.key);
    case 'tree': {
      // Πίσω η σειρά και οι γονείς του server· οι νέες κατηγορίες κρατούν τον γονέα τους, στο τέλος.
      if (!draft.tree) return draft;
      const parents = parentsOf(draft.tree);
      let tree = { roots: base.tree.roots.slice(), children: { ...base.tree.children } };
      Object.keys(tree.children).forEach(k => { tree.children[k] = tree.children[k].slice(); });
      draft.newCategories.forEach(key => {
        const parent = parents[key] ?? null;
        if (parent && (tree.children[parent] || base.categories[parent] || draft.newCategories.includes(parent))) {
          tree = { ...tree, children: { ...tree.children, [parent]: [...(tree.children[parent] || []), key] } };
        } else {
          tree = { ...tree, roots: [...tree.roots, key] };
        }
      });
      return { ...draft, tree };
    }
    case 'porder': {
      const productOrder = { ...draft.productOrder };
      delete productOrder[op.node];
      return { ...draft, productOrder };
    }
    case 'newGroup':
      return removeNewGroup(draft, op.ref);
    case 'newSizeGroup':
      return { ...draft, newSizeGroups: draft.newSizeGroups.filter(g => g.ref !== op.ref) };
    case 'newIngredient':
      return removeNewIngredient(draft, op.ref);
    default:
      return draft;
  }
}

/** Τα κλειδιά προϊόντων με αλλαγές (τελεία στη λίστα). */
export const editedOwners = (changes: ChangeEntry[]) => new Set(changes.map(c => c.owner));

/** Τα πεδία με αλλαγή ενός προϊόντος/κατηγορίας (κίτρινα). */
export function changedFields(changes: ChangeEntry[], owner: string): Set<string> {
  const out = new Set<string>();
  changes.forEach(c => { if (c.owner === owner && c.field) out.add(c.field); });
  return out;
}

/** Οι καρτέλες του πάνελ με αλλαγές. */
export function changedTabs(changes: ChangeEntry[], owner: string): Set<PanelTab> {
  const out = new Set<PanelTab>();
  changes.forEach(c => { if (c.owner === owner && c.tab) out.add(c.tab); });
  return out;
}
